import axios from 'axios';
import config from '@/config';
import useAuthStore from '@/stores/authStore';

/**
 * Lightweight pre-flight check used after loading a conversation to detect a
 * stale project before the user types anything. Returns
 * `{exists, accessible, name}`. Always 200 — non-existence is a normal
 * answer, not an error.
 */
export const fetchProjectStatus = async projectId => {
  const { data } = await axios.get(`/v1/analyst/project_status/${projectId}/`);
  return data;
};

/**
 * Fetch the pathway topology for a case (nodes, edges, optional balance flows).
 * Used to render the compact SVG diagram in the Assumptions view.
 */
export const fetchCaseTopology = async caseId => {
  const { data } = await axios.get(`/v1/analyst/topology/${caseId}/`);
  return data;
};

/**
 * Fetch the active assumptions for a case.
 * Used to refresh the Assumptions tab after direct param edits.
 */
export const fetchCaseAssumptions = async caseId => {
  const { data } = await axios.get(`/v1/analyst/assumptions/${caseId}/`);
  return data;
};

export const fetchAnalystConversations = async ({ limit = 25, offset = 0 } = {}) => {
  const { data } = await axios.get('/v1/analyst/conversations/', {
    params: { limit, offset },
  });
  return data;
};

export const fetchAnalystConversation = async conversationId => {
  const { data } = await axios.get(`/v1/analyst/conversations/${conversationId}/`);
  return data;
};

export const fetchAnalystConversationTurns = async conversationId => {
  const turns = [];
  let offset = 0;
  const limit = 100;

  while (true) {
    const { data } = await axios.get(`/v1/analyst/conversations/${conversationId}/turns/`, {
      params: { limit, offset },
    });
    const page = data.results ?? [];
    turns.push(...page);
    if (!data.next || page.length === 0) break;
    offset += page.length;
  }

  return turns;
};

export const deleteAnalystConversation = conversationId => axios.delete(`/v1/analyst/conversations/${conversationId}/`);

/**
 * Open an SSE stream to the Analyst endpoint and call event handlers as
 * data arrives.
 *
 * The first request creates a Conversation and Project on the server;
 * subsequent requests pass conversation_id to continue. Both IDs arrive in
 * the `project_id` event (which carries the project UUID despite its name).
 *
 * @param {object}   params
 * @param {string}   params.message
 * @param {string}   [params.conversationId]     - Continue a server conversation
 * @param {object}   [params.navigationContext]
 * @param {boolean}  [params.studyMode]          - Enable study mode (Opus + enhanced prompting)
 * @param {AbortSignal} [params.signal]
 * @param {Function} [params.onProjectId]        - Called with the project UUID string
 * @param {Function} [params.onText]
 * @param {Function} [params.onPlan]
 * @param {Function} [params.onActionStarted]
 * @param {Function} [params.onAction]
 * @param {Function} [params.onThinking]
 * @param {Function} [params.onDebugStats]
 * @param {Function} [params.onDone]
 * @param {Function} [params.onError]
 */
export const streamAnalystResponse = async ({
  message,
  conversationId,
  navigationContext,
  studyMode,
  signal,
  onProjectId,
  onText,
  onThinking,
  onPlan,
  onActionStarted,
  onAction,
  onDebugStats,
  onDone,
  onError,
}) => {
  const csrfToken = useAuthStore.getState().csrfToken;

  let response;
  try {
    response = await fetch(`${config.serverUrl}/v1/analyst/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken || '',
      },
      credentials: 'include',
      signal: signal ?? null,
      body: JSON.stringify({
        message,
        conversation_id: conversationId ?? null,
        navigation_context: navigationContext ?? null,
        study_mode: studyMode ?? false,
      }),
    });
  } catch (networkErr) {
    if (networkErr.name === 'AbortError') return;
    onError?.(`Network error: ${networkErr.message}`);
    return;
  }

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      detail = body?.error || body?.detail || detail;
    } catch (parseErr) {
      void parseErr;
    }
    onError?.(detail);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let aborted = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split('\n\n');
      buffer = parts.pop();

      for (const part of parts) {
        if (!part.trim()) continue;
        const dataLine = part.split('\n').find(l => l.startsWith('data: '));
        if (!dataLine) continue;
        let event;
        try {
          event = JSON.parse(dataLine.slice(6));
        } catch (parseErr) {
          void parseErr;
          console.warn('[Analyst SSE] Unparseable line:', dataLine);
          continue;
        }

        if (event.type === 'project_id') {
          // source: 'created' | 'supplied' | 'replaced'
          // previous_project_name present when replaced
          onProjectId?.(event.project_id, {
            conversationId: event.conversation_id ?? null,
            source: event.source ?? 'supplied',
            previousProjectName: event.previous_project_name ?? null,
          });
        } else if (event.type === 'text') {
          onText?.(event.content ?? '');
        } else if (event.type === 'thinking') {
          onThinking?.(event.content ?? '');
        } else if (event.type === 'plan') {
          onPlan?.(event.actions ?? []);
        } else if (event.type === 'action_started') {
          onActionStarted?.(event.action_type);
        } else if (event.type === 'action') {
          onAction?.(event.action_type, event.data);
        } else if (event.type === 'debug_stats') {
          onDebugStats?.(event.data ?? {});
        } else if (event.type === 'done') {
          onDone?.();
          return;
        } else if (event.type === 'error') {
          onError?.(event.content ?? 'Unknown error');
          return;
        }
      }
    }
  } catch (err) {
    if (err.name !== 'AbortError') throw err;
    aborted = true;
  } finally {
    reader.releaseLock();
  }

  if (!aborted) onDone?.();
};
